import spotifyConfig from './spotifyConfig';

// Token refresh listeners - allows other components to be notified when token refreshes
let tokenRefreshListeners = [];

export const onTokenRefresh = (callback) => {
  tokenRefreshListeners.push(callback);
  // Return unsubscribe function
  return () => {
    tokenRefreshListeners = tokenRefreshListeners.filter(cb => cb !== callback);
  };
};

// Generate random string for state parameter
const generateRandomString = (length) => {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return values.reduce((acc, x) => acc + possible[x % possible.length], '');
};

// Generate code challenge for PKCE
const sha256 = async (plain) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
};

const base64encode = (input) => {
  return btoa(String.fromCharCode(...new Uint8Array(input)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
};

export const redirectToSpotifyAuth = async () => {
  const codeVerifier = generateRandomString(64);
  const hashed = await sha256(codeVerifier);
  const codeChallenge = base64encode(hashed);

  // Store code verifier for later use
  window.localStorage.setItem('code_verifier', codeVerifier);
  window.sessionStorage.setItem('code_verifier', codeVerifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: spotifyConfig.clientId,
    scope: spotifyConfig.scopes.join(' '),
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
    redirect_uri: spotifyConfig.redirectUri,
  });

  window.location.href = `https://accounts.spotify.com/authorize?${params.toString()}`;
};

export const getAccessToken = async (code) => {
  const codeVerifier = window.localStorage.getItem('code_verifier');

  if (!codeVerifier) {
    console.error('No code verifier found in storage!');
    const sessionVerifier = window.sessionStorage.getItem('code_verifier');
    if (sessionVerifier) {
      console.log('Found verifier in sessionStorage');
      return sessionVerifier;
    }
    throw new Error('Code verifier not found. Please reconnect to Spotify.');
  }

  const params = new URLSearchParams({
    client_id: spotifyConfig.clientId,
    grant_type: 'authorization_code',
    code: code,
    redirect_uri: spotifyConfig.redirectUri,
    code_verifier: codeVerifier,
  });

  try {
    const response = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    const data = await response.json();
    
    if (data.access_token) {
      console.log('Successfully received tokens from Spotify');
      // Store tokens with expiry time
      window.localStorage.setItem('spotify_access_token', data.access_token);
      window.localStorage.setItem('spotify_refresh_token', data.refresh_token);
      window.localStorage.setItem('spotify_token_expiry', Date.now() + (data.expires_in * 1000));
      
      // Clear first-time flag when successfully connecting
      window.localStorage.removeItem('spotify_first_time');
      
      // Clear the code verifier since it's been used
      window.localStorage.removeItem('code_verifier');
      window.sessionStorage.removeItem('code_verifier');
      
      return data.access_token;
    } else {
      throw new Error('No access token received');
    }
  } catch (error) {
    console.error('Error getting access token:', error);
    throw error;
  }
};

// Check if this is user's first time connecting to Spotify
export const isFirstTimeSpotifyUser = () => {
  return !window.localStorage.getItem('spotify_first_time_shown');
};

// Mark that we've shown the first-time tutorial
export const markFirstTimeTutorialShown = () => {
  window.localStorage.setItem('spotify_first_time_shown', 'true');
};

// Check how many minutes until token expires
export const getMinutesUntilExpiry = () => {
  const expiry = window.localStorage.getItem('spotify_token_expiry');
  if (!expiry) return null;
  
  const expiryTime = parseInt(expiry);
  const now = Date.now();
  const msUntilExpiry = expiryTime - now;
  const minutesUntilExpiry = Math.floor(msUntilExpiry / 60000);
  
  return minutesUntilExpiry;
};

// Check if token is expired
export const isTokenExpired = () => {
  const minutesLeft = getMinutesUntilExpiry();
  return minutesLeft !== null && minutesLeft <= 0;
};

// Check if token expires soon (within specified minutes)
export const isTokenExpiringSoon = (withinMinutes = 5) => {
  const minutesLeft = getMinutesUntilExpiry();
  return minutesLeft !== null && minutesLeft > 0 && minutesLeft <= withinMinutes;
};

// UPDATED: Get stored access token, automatically refresh if expired
export const getStoredAccessToken = async () => {
  const token = window.localStorage.getItem('spotify_access_token');
  const expiry = window.localStorage.getItem('spotify_token_expiry');
  
  // No token at all
  if (!token) {
    return null;
  }
  
  // Token exists and is still valid (with 5-minute buffer)
  if (expiry && Date.now() < parseInt(expiry) - (5 * 60 * 1000)) {
    return token;
  }
  
  // Token expired - return null (user needs to reconnect)
  return null;
};

// Clean up any incorrect localStorage keys from previous versions
export const migrateSpotifyKeys = () => {
  // Remove any incorrectly named keys
  const incorrectKeys = [
    'spotify_token_expires_at',
    'spotify_code_verifier',
    'GDPR_REMOVAL_FLAG'
  ];
  
  incorrectKeys.forEach(key => {
    if (window.localStorage.getItem(key)) {
      console.log(`Removing incorrect key: ${key}`);
      window.localStorage.removeItem(key);
    }
  });
};

export const clearSpotifyAuth = () => {
  window.localStorage.removeItem('spotify_access_token');
  window.localStorage.removeItem('spotify_refresh_token');
  window.localStorage.removeItem('spotify_token_expiry');
  window.localStorage.removeItem('code_verifier');
  window.sessionStorage.removeItem('code_verifier');
  
  // Also clean up any incorrect keys
  migrateSpotifyKeys();
};